import Papa from 'papaparse';
import { MAX_URLS, URL_LIMIT_ERROR } from './constants';

// URL validation regex pattern
const URL_PATTERN = new RegExp(
    '^' + // Start of string
    '(?:(?:https?:)?\\/?\\/?)?' + // Protocol and slashes are optional
    '(?:\\S+(?::\\S*)?@)?' + // Authentication (optional)
    '(?:' +
    '(?!(?:10|127)(?:\\.\\d{1,3}){3})' + // Exclude private ranges
    '(?!(?:169\\.254|192\\.168)(?:\\.\\d{1,3}){2})' +
    '(?!172\\.(?:1[6-9]|2\\d|3[0-1])(?:\\.\\d{1,3}){2})' +
    '(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])' +
    '(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}' +
    '(?:\\.(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))' +
    '|' +
    '(?:' + // Host name
    '(?:' +
    '[a-z0-9\\u00a1-\\uffff]' +
    '[a-z0-9\\u00a1-\\uffff_-]{0,62}' +
    ')?' +
    '[a-z0-9\\u00a1-\\uffff]\\.' +
    ')+' + // TLD identifier
    '(?:[a-z\\u00a1-\\uffff]{2,}\\.?)' +
    ')' +
    '(?::\\d{2,5})?' + // Port (optional)
    '(?:[/?#]\\S*)?' + // Path (optional)
    '$', 'i' // Case-insensitive
);

/**
 * Validates if a string is a properly formatted URL
 * @param {string} url - The URL to validate
 * @returns {boolean} - Whether the URL is valid
 */
const isValidUrl = (url) => {
    if (!url || typeof url !== 'string') return false;

    // Trim whitespace
    let cleanUrl = url.trim();

    try {
        // Try to fix common issues
        if (!cleanUrl.includes('://')) {
            // If no protocol is specified, add https://
            if (!cleanUrl.startsWith('http')) {
                cleanUrl = 'https://' + cleanUrl;
            } else {
                // Fix http: or https: without slashes
                cleanUrl = cleanUrl.replace(/^(https?:)(?!\/)/, '$1//');
            }
        }

        // Test against regex pattern
        if (!URL_PATTERN.test(cleanUrl)) return false;

        // Final validation using URL constructor
        new URL(cleanUrl);
        return true;
    } catch {
        return false;
    }
};

/**
 * Normalizes a URL by ensuring it has a proper protocol
 * @param {string} url - The URL to normalize
 * @returns {string} - The normalized URL
 */
const normalizeUrl = (url) => {
    let normalizedUrl = url.trim();

    // Add protocol if missing
    if (!normalizedUrl.includes('://')) {
        if (normalizedUrl.startsWith('http:')) {
            normalizedUrl = normalizedUrl.replace(/^http:(?!\/)/, 'http://');
        } else if (normalizedUrl.startsWith('https:')) {
            normalizedUrl = normalizedUrl.replace(/^https:(?!\/)/, 'https://');
        } else {
            normalizedUrl = 'https://' + normalizedUrl;
        }
    }

    return normalizedUrl;
};

/**
 * Checks if the file is a valid CSV format
 * @param {File} file - The file to validate
 * @returns {boolean} - Whether the file is a valid CSV
 */
export const isValidCSV = (file) => {
    const fileExtension = file.name.split('.').pop().toLowerCase();
    return fileExtension === 'csv' ||
        file.type === 'text/csv' ||
        file.type === 'application/vnd.ms-excel' ||
        file.type === 'application/csv' ||
        file.type === 'text/x-csv' ||
        file.type === 'application/x-csv';
};

/**
 * Validates the content of a CSV file
 * @param {File} file - The CSV file to validate
 * @returns {Promise<{isValid: boolean, normalizedFile: File}>}
 */
export const validateCSVContent = (file) => {
    return new Promise((resolve, reject) => {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                // Validate CSV structure
                const fields = results.meta.fields || [];
                const urlColumn = fields.find(field => field.toLowerCase() === 'url');

                if (!urlColumn) {
                    const foundColumns = fields.join(', ');
                    reject({
                        message: 'Invalid CSV Structure',
                        type: 'header_error',
                        details: {
                            foundColumns,
                            expectedColumn: 'url'
                        },
                        message_text: `Your CSV file must have a column named "url". Found these columns instead: ${foundColumns}. Please rename your column to "url" and try again.`
                    });
                    return;
                }

                // Normalize data and validate URLs
                const errors = [];
                const normalizedData = [];
                let validUrlCount = 0;

                // If column exists but with different case, rename it to lowercase 'url'
                results.data.forEach((row, index) => {
                    const url = (urlColumn !== 'url' ? row[urlColumn] : row.url)?.trim();

                    if (!url) {
                        errors.push({
                            row: index + 2, // +2 for 1-based indexing and header row
                            url: '',
                            message: 'Empty URL'
                        });
                    } else {
                        try {
                            // Try to normalize and validate the URL
                            const normalizedUrl = normalizeUrl(url);
                            if (!isValidUrl(normalizedUrl)) {
                                throw new Error('Invalid URL format');
                            }
                            validUrlCount++;
                            normalizedData.push({
                                url: normalizedUrl // Always use lowercase 'url' as the column name
                            });
                        } catch (e) {
                            errors.push({
                                row: index + 2,
                                url,
                                message: 'Invalid URL format'
                            });
                        }
                    }
                });

                // Check URL limit
                if (validUrlCount > MAX_URLS) {
                    reject(new Error(URL_LIMIT_ERROR));
                    return;
                }

                if (errors.length > 0) {
                    reject({
                        message: 'Invalid URLs found in CSV',
                        errors,
                        invalidCount: errors.length,
                        totalCount: results.data.length
                    });
                } else {
                    // Create a new CSV blob with normalized URLs
                    const normalizedCsv = new Blob(
                        [Papa.unparse(normalizedData)],
                        { type: 'text/csv;charset=utf-8;' }
                    );

                    resolve({
                        isValid: true,
                        urlCount: validUrlCount,
                        normalizedFile: new File(
                            [normalizedCsv],
                            file.name,
                            { type: 'text/csv' }
                        )
                    });
                }
            },
            error: (error) => {
                reject(new Error('Error parsing CSV file: ' + error.message));
            }
        });
    });
};

/**
 * Generates an example CSV file with valid URLs
 */
export const downloadExampleCSV = () => {
    const csvContent = 'url\n' +
        'https://example.com\n' +
        'https://www.example.com\n' +
        'http://example.com\n' +
        'http://www.example.com\n' +
        'www.example.com\n' +
        'example.com';

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'csv_sample.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};